import { useRef, useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Eraser, Check } from "lucide-react";

/**
 * Dokunmatik/mouse imza alanı. onChange(dataUrl|null) döner.
 * value verilirse (data URL) salt-önizleme gösterilir.
 */
export function SignaturePad({ label = "İmza", value = null, onChange, testId = "sign", height = 160, readOnly = false }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [dirty, setDirty] = useState(false);

  const setupCanvas = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * ratio;
    c.height = height * ratio;
    const ctx = c.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0f172a";
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, height);
  }, [height]);

  useEffect(() => {
    setupCanvas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (e) => {
    if (readOnly) return;
    e.preventDefault();
    drawing.current = true;
    last.current = pos(e);
  };
  const move = (e) => {
    if (!drawing.current || readOnly) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (!dirty) setDirty(true);
  };
  const end = () => {
    if (!drawing.current || readOnly) return;
    drawing.current = false;
    if (dirty && onChange) onChange(canvasRef.current.toDataURL("image/png"));
  };
  const clear = () => {
    setupCanvas();
    setDirty(false);
    if (onChange) onChange(null);
  };

  if (readOnly) {
    return (
      <div data-testid={`${testId}-preview`}>
        <div className="mb-1 text-xs font-medium text-slate-600">{label}</div>
        {value ? (
          <img src={value} alt={label} className="w-full rounded-lg border border-slate-200 bg-white" style={{ height }} />
        ) : (
          <div className="flex w-full items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50 text-xs text-slate-400" style={{ height }}>
            İmza yok
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid={`${testId}-pad`}>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-medium text-slate-600">{label}</span>
        {(dirty || value) && (
          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600">
            <Check className="h-3 w-3" /> imzalandı
          </span>
        )}
      </div>
      <canvas
        ref={canvasRef}
        data-testid={`${testId}-canvas`}
        className="w-full touch-none rounded-lg border-2 border-dashed border-slate-300 bg-white"
        style={{ height }}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
      <Button type="button" variant="ghost" size="sm" onClick={clear} data-testid={`${testId}-clear`}
        className="mt-1 h-7 text-xs text-slate-500 hover:text-red-600">
        <Eraser className="mr-1 h-3 w-3" /> Temizle
      </Button>
    </div>
  );
}

export default SignaturePad;
